import React, { useMemo, useRef } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  CardHeader,
  Link,
  Pagination,
  Skeleton,
  Toolbar,
  Typography,
} from "@mui/material";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useGaleSearch } from "./GaleSearchProvider";
import { SearchRetrieveRecord } from "./SearchRetrieveRecord";
import { useNumberFormat } from "../../../../hooks/useNumberFormat";
import { SanitizedHTML } from "@tlc/ls2pac/components/SanitizedHTML";
import { useSkipLinks } from "../../../../hooks/useSkipLinks";

export const GaleResultsPage = () => {
  const {
    searchParams: { term, page },
    setPage,
    totalHits,
    results,
    loadingResults,
    pageSize,
  } = useGaleSearch();

  const { translate } = useLanguage();
  const { format } = useNumberFormat();
  const galeResultsHeaderRef = useRef<HTMLHeadingElement | null>(null);

  useSkipLinks([
    {
      label: translate("SkipToResults"),
      targetRef: galeResultsHeaderRef,
      priority: 10,
    },
  ]);

  return (
    <Box>
      <Toolbar
        sx={{
          flexWrap: "wrap",
          gap: 1,
          pl: { sm: 1, xs: 1 },
          pr: { xs: 1, sm: 1 },
        }}
      >
        <Typography
          tabIndex={-1}
          ref={galeResultsHeaderRef}
          color="inherit"
          variant="h6"
          component="h2"
        >
          {loadingResults ? (
            <Skeleton
              variant="text"
              width={20}
              sx={{ display: "inline-flex" }}
            />
          ) : (
            format(totalHits ?? 0)
          )}{" "}
          {translate("searchResultsFor")} "{term}"
        </Typography>
      </Toolbar>
      {loadingResults ? (
        <>
          {Array.from({ length: pageSize }).map((_, index) => (
            <div key={index}>
              <GaleResultSkeleton />
            </div>
          ))}
        </>
      ) : (
        <>
          {totalHits && totalHits > 0 ? (
            results &&
            results.map((result, index) => (
              <div key={index}>
                <GaleResultDisplay result={result} />
              </div>
            ))
          ) : (
            <Alert severity="info">
              <AlertTitle>{translate("searchErrorMessage")}</AlertTitle>
            </Alert>
          )}
        </>
      )}
      {(totalHits ?? 0) > pageSize && (
        <Pagination
          page={page}
          count={Math.ceil((totalHits ?? 0) / pageSize)}
          onChange={(_: React.ChangeEvent<unknown>, value: number) => {
            setPage(value);
          }}
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            paddingTop: "30px",
            paddingBottom: "50px",
          }}
          aria-label="Gale results pagination"
        />
      )}
    </Box>
  );
};

export const GaleResultSkeleton = () => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Skeleton
            animation="wave"
            height={14}
            width="80%"
            style={{ marginBottom: 6 }}
          />
        }
      />{" "}
      <CardContent>
        <React.Fragment>
          <Typography>
            <Skeleton animation="wave" width="60%" height={10} />
          </Typography>

          <Typography>
            <Skeleton animation="wave" width="75%" height={10} />
          </Typography>
        </React.Fragment>
      </CardContent>
    </Card>
  );
};

interface GaleResultDisplayProps {
  result: SearchRetrieveRecord;
}

export const GaleResultDisplay: React.FC<GaleResultDisplayProps> = ({
  result,
}) => {
  const { title, identifiers, relation, bibliographicCitation, descriptions } =
    result;

  const titleLink = useMemo(() => {
    const firstIdentifier = identifiers[0];
    if (firstIdentifier?.uri) {
      return (
        <Link
          href={firstIdentifier.uri as string}
          target="_blank"
          rel="noopener noreferrer"
          component="a"
        >
          <SanitizedHTML html={title} />
        </Link>
      );
    }
    return <SanitizedHTML html={title} />;
  }, [identifiers, title]);

  const galeSource = useMemo(() => {
    return `${relation} ${bibliographicCitation}`;
  }, [relation, bibliographicCitation]);

  const summaryArray = useMemo<string[]>(() => {
    if (descriptions.length === 0) return [];
    return descriptions;
  }, [descriptions]);

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        slotProps={{
          title: {
            component: "h3",
          },
        }}
        title={titleLink}
        subheader={
          <Typography component="h4" color="text.secondary">
            {galeSource}
          </Typography>
        }
      />
      <CardContent>
        {/* Only render summaries if we have them to render  */}
        {summaryArray.length > 0 && (
          <Typography component="p">
            {summaryArray.map((d, index) => (
              <SanitizedHTML key={index} html={d} />
            ))}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
